import { AppConfig } from '@/config/appConfig';
import { clearQueryCache } from '@/lib/reactQueryHelpers/activeQueryClient';
import { Dexie, Table } from 'dexie';
import localforage from 'localforage';
import { create } from 'zustand';
import { createJSONStorage, persist, StateStorage } from 'zustand/middleware';
// Define TypeScript interfaces for authentication related data
export interface User {
  id: string | number;
  name?: string;
  email: string;
  role?: string;
  org_id?: string | number;
  custom_fields?: any;
  org?: any;
}

export interface AuthState {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  originalUserRole: string | null;
  shouldCleanAuthParams: boolean; // Flag to trigger cleanup of auth params (token, jwt) from URL
  // isRedirectToV3?: any;
  _hasHydrated: boolean;
  queryParamsToken: string | null;
  initialUrlWithToken: string | null;
  // Mac-app login bridge: timestamp (ms epoch) when /auth/login was
  // entered with ?from_mac_app=1. Used by handleAuthSuccess to decide
  // whether to top-level-nav to verify_app after login. Null = inactive.
  // The TTL check (10 min) lives in selectIsMacAppLoginActive below.
  macAppLoginAt: number | null;
}

export interface AuthStoreConfig {
  authApiBaseUrl: string;
  storageKey: string;
  getAuthHeaders?: (token: string | null) => Record<string, string>;
  validateToken?: (token: string | null) => Promise<boolean>;
  dbName: string;
  storeName: string;
}

export interface LoginCredentials {
  email?: string;
  password?: string;
  // Add any other login fields
}

// Define the shape of the authentication state
export const initialAuthState: AuthState = {
  token: null,
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  originalUserRole: null,
  shouldCleanAuthParams: false,
  _hasHydrated: false,
  queryParamsToken: null,
  initialUrlWithToken: null,
  macAppLoginAt: null,
  // isRedirectToV3: false,
};

// Mac-app login TTL — bounds how long `macAppLoginAt` is treated as
// active. Defends against a stale `?from_mac_app=1` URL leaking into
// a non-Mac-app browser, where the armed flag would otherwise persist
// indefinitely in localforage and bridge a later normal login. Not
// the same as the BE `Services::PkceChallenge.TTL` (60s).
export const MAC_APP_LOGIN_TTL_MS = 10 * 60 * 1000;

// Configuration options for the auth store
export const authStoreConfig: AuthStoreConfig = {
  // Base URL for authentication API endpoints
  authApiBaseUrl: AppConfig.apiUrl,
  // Key for local storage persistence
  storageKey: AppConfig.tokenKey,
  // Function to get headers, useful for adding token to requests
  // getAuthHeaders: (token: string | null) => (token ? { Authorization: `Bearer ${token}` } : {}),
  // // Function to validate token (optional, can be done server-side)
  // validateToken: async (token: string | null): Promise<boolean> => {
  //   if (!token) return false;
  //   try {
  //     // Example: Ping a protected endpoint to check token validity
  //     const response = await api.get(`${authStoreConfig.authApiBaseUrl}/get_user.json`, {
  //       headers: authStoreConfig.getAuthHeaders(token),
  //     });
  //     return response.status === 200;
  //   } catch (err) {
  //     console.error('Token validation failed:', err);
  //     return false;
  //   }
  // },
  // Dexie DB name for persistence
  dbName: 'apDB',
  // Dexie store name for auth state
  storeName: 'authState',
};

type AuthTable = {
  id: string;
  state: AuthState;
};

type UserAnswersState = {
  user_answer_id: string;
  user_answer_state: string;
};

type UserAnswersTable = {
  id: string;
  state: UserAnswersState;
};

class AssessPrepDB extends Dexie {
  authState!: Table<AuthTable, string>; // Table to store auth state
  userAnswersState!: Table<UserAnswersTable, string>; // Table to store user answers state
  constructor(dbName: string) {
    super(dbName);
    this.version(1).stores({
      authState: '&id, state', // Primary key is 'id'
      userAnswersState: '&id, state', // Primary key is 'id'
    });
  }
}

// Create an instance of the database
const db = new AssessPrepDB(authStoreConfig.dbName) as AssessPrepDB & {
  authState: Table<AuthTable, string>;
  userAnswersState: Table<UserAnswersTable, string>;
};

type StoreNames = 'authState' | 'userAnswersState';

const createDexieStorage: any = (db: AssessPrepDB, storeName: StoreNames): StateStorage => {
  const finalStorage: StateStorage = {
    getItem: async (name: string): Promise<string | null> => {
      console.log('DexieStorage: getItem', name);
      try {
        // We'll store the state with a fixed ID, e.g., 'current'
        const record = await db[storeName].get('current');
        return record ? JSON.stringify(record.state) : null;
      } catch (error) {
        console.error('DexieStorage: Error getting item', error);
        return null;
      }
    },
    setItem: async (name: string, value: string): Promise<void> => {
      console.log('DexieStorage: setItem', name, value);
      try {
        const state = JSON.parse(value);
        console.log('DexieStorage: setItem 126', state);
        // Store the state with a fixed ID
        await db[storeName].put({ id: 'current', state });
      } catch (error) {
        console.error('DexieStorage: Error setting item', error);
      }
    },
    removeItem: async (name: string): Promise<void> => {
      console.log('DexieStorage: removeItem', name);
      try {
        // Remove the state with the fixed ID
        await db[storeName].delete('current');
      } catch (error) {
        console.error('DexieStorage: Error removing item', error);
      }
    },
  };
  return finalStorage;
};

const dexieAuthStore = createDexieStorage(db, 'authState');
const dexieUserAnswersStore = createDexieStorage(db, 'userAnswersState');

// // Custom Zustand StateStorage implementation using Dexie
// export const dexieStorage: StateStorage = {
//   getItem: async (name: string): Promise<string | null> => {
//     console.log('DexieStorage: getItem', name);
//     try {
//       // We'll store the state with a fixed ID, e.g., 'current'
//       const record = await db.authState.get('current');
//       return record ? JSON.stringify(record.state) : null;
//     } catch (error) {
//       console.error('DexieStorage: Error getting item', error);
//       return null;
//     }
//   },
//   setItem: async (name: string, value: string): Promise<void> => {
//     console.log('DexieStorage: setItem', name);
//     try {
//       const state = JSON.parse(value);
//       // Store the state with a fixed ID
//       await db.authState.put({ id: 'current', state });
//     } catch (error) {
//       console.error('DexieStorage: Error setting item', error);
//     }
//   },
//   removeItem: async (name: string): Promise<void> => {
//     console.log('DexieStorage: removeItem', name);
//     try {
//       // Remove the state with the fixed ID
//       await db.authState.delete('current');
//     } catch (error) {
//       console.error('DexieStorage: Error removing item', error);
//     }
//   },
// };

// Define the actions available in the auth store
interface AuthActions {
  setAuthState: (state: Partial<AuthState>) => void;
  setCurrentUser: (user: User) => void;
  login?: (credentials: LoginCredentials) => Promise<boolean>;
  logout: () => Promise<void>;
  checkAuth?: () => Promise<void>;
  waitForHydration: () => Promise<void>;
  flushToPersistence: () => Promise<void>;
}
// Combine state and actions for the store type
type AuthStore = AuthState & AuthActions & { config: AuthStoreConfig };

const localForageAuthStorage = localforage.createInstance({
  name: authStoreConfig.storeName,
  // name: 'localForageApStore',
});
// Create the authentication store with persistence middleware
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...initialAuthState,
      config: authStoreConfig, // Store configuration within the state (optional, but can be useful)

      // Action to set authentication state
      setAuthState: (state: Partial<AuthState>) => set(state),

      setCurrentUser: (user: User) => set({ user }),
      // Action to handle login
      // login: async (credentials: LoginCredentials): Promise<boolean> => {
      //   set({ loading: true, error: null });
      //   try {
      //     const response = await axiosInstance.post<{ token: string; user: User }>(`${get().config.authApiBaseUrl}/auth/login`, credentials);
      //     const { token, user } = response.data;
      //     set({ token, user, isAuthenticated: true, loading: false, error: null });
      //     return true; // Indicate success
      //   } catch (err: any) {
      //     set({ loading: false, error: err.message || 'Login failed' });
      //     return false; // Indicate failure
      //   }
      // },

      // Action to handle logout. Preserves macAppLoginAt when active
      // — Mac-app context is independent of user-session state.
      logout: async (): Promise<void> => {
        const current = get();
        set({
          ...initialAuthState,
          _hasHydrated: true,
          macAppLoginAt: selectIsMacAppLoginActive(current) ? current.macAppLoginAt : null,
        });
        // After auth state clears: the <Refine key> holds across logout, so the
        // QueryClient survives — with staleTime Infinity a same-user relogin
        // would read every pre-logout query forever. Clear is the freshness seam
        // (R2-G62 follow-up; refineKey.ts).
        clearQueryCache();
      },
      waitForHydration: async (): Promise<void> => {
        return new Promise((resolve) => {
          const checkHydration = () => {
            if (get()._hasHydrated) {
              resolve();
            } else {
              setTimeout(checkHydration, 10);
            }
          };
          checkHydration();
        });
      },
      // Explicitly write the current persisted slice to IndexedDB and AWAIT
      // the write. Zustand's persist middleware writes asynchronously after
      // every set(), but exposes no Promise for that write — so a
      // setAuthState(...) followed immediately by window.location.replace(...)
      // can race the write and reload the next page from a stale (or empty)
      // store. Call this whenever you need to guarantee persistence before a
      // top-level navigation.
      flushToPersistence: async (): Promise<void> => {
        const s = get();
        const value = JSON.stringify({
          state: {
            token: s.token,
            originalUserRole: s.originalUserRole,
            queryParamsToken: s.queryParamsToken,
            macAppLoginAt: s.macAppLoginAt,
          },
          version: 0,
        });
        await localForageAuthStorage.setItem(authStoreConfig.storeName, value);
      },

      // Action to check authentication status (e.g., on app load)
      // checkAuth: async (): Promise<void> => {
      //   const currentToken = get().token;
      //   if (currentToken) {
      //     set({ loading: true, error: null });
      //     const isValid = await get().config.validateToken(currentToken);
      //     if (isValid) {
      //       // Optionally fetch user details again if needed
      //       try {
      //         const response = await axiosInstance.get<User>(`${get().config.authApiBaseUrl}/get_user.json`, {
      //           headers: get().config.getAuthHeaders(currentToken)
      //         });
      //         set({ user: response.data, isAuthenticated: true, loading: false });
      //       } catch (err) {
      //         console.error('Failed to fetch user details on checkAuth:', err);
      //         set(initialAuthState); // Clear state if fetching user fails
      //       }
      //     } else {
      //       set(initialAuthState); // Clear state if token is invalid
      //     }
      //     set({ loading: false });
      //   } else {
      //     set(initialAuthState); // Ensure state is clear if no token
      //   }
      // },
    }),
    {
      name: authStoreConfig.storeName, // unique name
      // storage: createJSONStorage(() => dexieAuthStore), // Use local storage
      storage: createJSONStorage(() => localForageAuthStorage), // Use local storage
      // Optional: Specify which parts of the state to persist
      partialize: (state) => ({
        token: state.token,
        // jwtToken: state.jwtToken, // Add jwtToken to persisted state
        // user: state.user, // Persist user details too
        // isAuthenticated: state.isAuthenticated, // Persist auth status
        // partnerName: state.partnerName,
        originalUserRole: state.originalUserRole,
        queryParamsToken: state.queryParamsToken, // Persist queryParamsToken to survive page reloads
        macAppLoginAt: state.macAppLoginAt, // Survive the SSO round-trip page reload
        // isRedirectToV3: state.isRedirectToV3,
      }),
      onRehydrateStorage: () => (state) => {
        // This callback runs after the state has been rehydrated from storage
        if (state) {
          state._hasHydrated = true;
        }
      },
      // Optional: Migration function if state structure changes
      // version: 1,
      // migrate: (persistedState: any, version: number) => {
      //   if (version === 0) {
      //     // migrate v0 to v1
      //   }
      //   return persistedState;
      // },
    }
  )
);

// Export selectors separately for better type inference and usage

export const selectCurrentUser: any = (state: AuthStore) => state.user;

export const selectCurrentUserRole: any = (state: AuthStore) => state.user?.role;

export const selectOriginalUserRole = (state: AuthStore) => state.originalUserRole;

export const selectIsAuthenticated = (state: AuthStore) => state.isAuthenticated;

export const selectAuthLoading = (state: AuthStore) => state.loading;

export const selectAuthError = (state: AuthStore) => state.error;

export const selectAuthToken = (state: AuthStore) => state.token;

export const selectShouldCleanAuthParams = (state: AuthStore) => state.shouldCleanAuthParams;

export const selectEnabledFeatures = (state: AuthStore) => state.user?.org?.enabled_features;

export const selectQueryParamsToken = (state: AuthStore) => state.queryParamsToken;

export const selectInitialUrlWithToken = (state: AuthStore) => state.initialUrlWithToken;

// Mac-app login bridge: returns true if /auth/login was entered with
// ?from_mac_app=1 within the last MAC_APP_LOGIN_TTL_MS. Returns false
// (and the caller should treat the flag as gone) once the TTL has
// elapsed — handleAuthSuccess will not re-hit verify_app for stale
// flags.
export const selectIsMacAppLoginActive = (state: AuthStore): boolean => {
  if (!state.macAppLoginAt) return false;
  return Date.now() - state.macAppLoginAt < MAC_APP_LOGIN_TTL_MS;
};

// Arms macAppLoginAt (=now) when the URL carries `?from_mac_app=1` AND
// strips the flag from the URL via history.replaceState. The flag is
// the one-shot trigger; once the value lives in the store (with TTL),
// keeping it in the URL would just keep re-firing the arm on every
// check() / mount and clutter the URL bar. No-op when the flag is
// absent. Single URL reader — every other Mac-app context check goes
// through `selectIsMacAppLoginActive` against the store.
export const initiateMacAppLogin = (): void => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has('from_mac_app')) return;
  useAuthStore.getState().setAuthState({ macAppLoginAt: Date.now() });
  url.searchParams.delete('from_mac_app');
  window.history.replaceState({}, '', url.toString());
};
